"use client";

import React, { useRef } from "react";
import Image from "next/image";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { usePathname } from "next/navigation";
import Link from "next/link";
import { ContainerProp } from "../../../global";
import BottomCurves from "../curves/bottomCurves";
import BannerFloatingArrow from "../uiDecor/bannerFloatingArrow";
import { getStrapiMedia } from "@/utils/api-helpers";
import { textTurquoise } from "@/app/utils/api-helpers";

type BannerDescContainerProp = Omit<ContainerProp, "className"> & {
  waveBgColor: { waveBgColor: string };
};

export default function HeroContainer({
  data,
}: {
  data: BannerDescContainerProp;
}) {
  const containerRef = useRef(null);
  const imgRef = useRef(null);
  const pathname = usePathname();

  const generateBreadcrumb = () => {
    const pathSegments = pathname.split("/").filter(Boolean);
    const breadcrumbs = [{ name: "Home", href: "/" }];

    pathSegments.forEach((segment, index) => {
      const href = "/" + pathSegments.slice(0, index + 1).join("/");
      const name = segment
        .split("-")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
      breadcrumbs.push({ name, href });
    });

    return breadcrumbs;
  };

  const breadcrumbs = generateBreadcrumb();

  useGSAP(
    () => {
      gsap.to(imgRef.current, { opacity: 1, stagger: 0.3, y: 0 });
      gsap.to(".textChild", { opacity: 1, stagger: 0.3, y: 0 });
    },
    { scope: containerRef },
  );

  return (
    <>
      <link
        rel="preload"
        href={getStrapiMedia(data.image!.url)}
        as="image"
      />
      <section
        className={`pt-24 bg-midGradientPurple ${data.extraMargin
            ? "md:h-[40lvw] min-h-[500px] sm:pt-32 lg:pt-[3.969vw] pb-20"
            : "md:h-[30lvw] min-h-[360px] lg:pt-[8.222vw]"
          }`}
      >
        <div
          className={`flex flex-col md:flex-row gap-y-6 md:gap-y-0 ${data.extraMargin ? "lg:items-center" : "lg:items-start"
            }`}
          ref={containerRef}
        >
          <div
            className={`flex-1 ${(data.description && data.extraMargin) ||
                data.extraMargin
                ? "md:-translate-y-14"
                : "md:-translate-y-14"
              }`}
          >
            <nav className="textChild opacity-0 translate-y-8 mb-12">
              <ol className="flex items-start text-sm text-white/70">
                {breadcrumbs.map((crumb, index) => (
                  <li
                    key={crumb.href}
                    className="flex items-start"
                  >
                    {index > 0 && (
                      <span className="mx-2">&gt;</span>
                    )}
                    {index === breadcrumbs.length - 1 ? (
                      <span className="text-white">
                        {crumb.name}
                      </span>
                    ) : (
                      <Link
                        href={crumb.href}
                        className="hover:text-white transition-colors"
                      >
                        {crumb.name}
                      </Link>
                    )}
                  </li>
                ))}
              </ol>
            </nav>
            {data.serviceName && (
              <h1 className="textChild opacity-0 translate-y-8 !text-[clamp(18px,1.250vw,1.250vw)] !leading-relaxed !tracking-normal !font-light text-turquoise">
                {data.serviceName}
              </h1>
            )}
            {data.header && (
              <h1 className="textChild opacity-0 translate-y-8 md:w-[90%] !text-[clamp(40px,3.750vw,3.750vw)] !leading-none !tracking-tight !font-bold">
                {textTurquoise(data.header)}
              </h1>
            )}
            {data.description && (
              <div className="lg:w-10/12">
                <p className="textChild text-white opacity-0 translate-y-8">
                  {data.description}
                </p>
              </div>
            )}
          </div>
          <div
            className={`flex-1 ${!data.extraMargin && "lg:-translate-y-20"
              }`}
          >
            {data.image && (
              <Image
                src={getStrapiMedia(data.image.url)}
                width={800}
                height={800}
                alt={data.image.alternativeText || ""}
                className={`opacity-0 translate-y-8 m-auto w-full h-auto z-1`}
                ref={imgRef}
                priority
              />
            )}
          </div>
        </div>
        <BannerFloatingArrow
          direction={"left"}
          className={`${data.extraMargin
              ? "!-bottom-2 md:!-bottom-12 lg:!-bottom-4"
              : "!-bottom-4 md:!-bottom-8"
            }`}
          extraMargin={data.extraMargin}
        />
        <BottomCurves
          color={data.waveBgColor.waveBgColor}
          direction="left"
        />
      </section>
    </>
  );
}
